import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Shield, Lock, Thermometer, Loader2, ChevronRight, Hash, Truck, Clock } from 'lucide-react';

export default function PassportViewerPage() {
  const [passports, setPassports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPassport, setSelectedPassport] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchPassports();
     
  }, []);

  const fetchPassports = async () => {
    try {
      setLoading(true);
      const res = await opsApi.listPassports({});
      setPassports(res.data || []);
    } catch (err) {
      toast.error('Failed to load passports');
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (passport) => {
    try {
      setSelectedPassport(passport);
      setDetailLoading(true);
      const res = await opsApi.getPassportDetail(passport.id);
      setDetail(res.data);
    } catch (err) {
      toast.error('Failed to load passport detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="passport-viewer-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Passport Chain of Custody</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">Complete chain of custody with SHA-256 integrity seals</p>
      </div>

      {passports.length === 0 ? (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-8 text-center text-slate-500">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No passports yet. Activate labels from the Dispatcher Workflow to create passports.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {passports.map(p => (
            <Card key={p.id} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewDetail(p)}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <p className="font-mono text-sm text-slate-900 dark:text-white">{p.passport_uuid.slice(0, 16)}...</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {p.invoice_number && <span>Invoice: {p.invoice_number}</span>}
                      <span>Entries: {p.total_entries}</span>
                      <span>{formatDate(p.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={p.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}>
                    {p.status}
                  </Badge>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Passport Detail Modal */}
      <Dialog open={!!selectedPassport} onOpenChange={() => { setSelectedPassport(null); setDetail(null); }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-500" />Passport Detail
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">UUID</span>
                  <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{detail.passport_uuid}</span>
                </div>
                {detail.invoice_number && <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="text-slate-900 dark:text-white">{detail.invoice_number}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Status</span><Badge className="bg-green-100 text-green-700">{detail.status}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-500">Created</span><span>{formatDate(detail.created_at)}</span></div>
                {detail.master_hash && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" />Chain Hash</span>
                    <span className="font-mono text-xs text-teal-600">{detail.master_hash.slice(0, 24)}...</span>
                  </div>
                )}
              </div>

              <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Hash className="w-4 h-4 text-teal-500" />Chain Entries ({detail.entries?.length || 0})
              </h3>

              {detail.entries?.map((entry, idx) => (
                <div key={entry.id} className="relative pl-8">
                  {/* Timeline connector */}
                  {idx < detail.entries.length - 1 && (
                    <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-teal-200 dark:bg-teal-800" />
                  )}
                  <div className="absolute left-0 top-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{entry.entry_number}</span>
                  </div>

                  <div className={cn("bg-white dark:bg-slate-700/50 border rounded-lg p-4", entry.locked ? "border-green-300 dark:border-green-700" : "border-slate-200 dark:border-slate-600")}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={entry.entry_type === 'transit' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'}>
                        {entry.entry_type === 'transit' ? <><Truck className="w-3 h-3 mr-1" />Transit</> : <><Clock className="w-3 h-3 mr-1" />HUB Timeslice</>}
                      </Badge>
                      <Badge className={
                        entry.status === 'active' ? 'bg-green-100 text-green-700' :
                        entry.status === 'locked' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }>
                        {entry.locked && <Lock className="w-3 h-3 mr-1" />}
                        {entry.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-400">
                      {entry.device_uid && <div><span className="text-slate-400">Device:</span> <span className="font-mono">{entry.device_uid}</span></div>}
                      {entry.custodian_name && <div><span className="text-slate-400">Custodian:</span> {entry.custodian_name}</div>}
                      {entry.location_name && <div><span className="text-slate-400">Location:</span> {entry.location_name}</div>}
                      <div><span className="text-slate-400">Started:</span> {formatDate(entry.started_at)}</div>
                    </div>

                    {(entry.temp_min !== null || entry.temp_max !== null) && (
                      <div className="mt-2 flex items-center gap-3 text-xs">
                        <Thermometer className="w-3 h-3 text-teal-500" />
                        <span>Min: {entry.temp_min?.toFixed(1)}°C</span>
                        <span>Max: {entry.temp_max?.toFixed(1)}°C</span>
                        <span>Avg: {entry.temp_avg?.toFixed(1)}°C</span>
                        {entry.breach_count > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{entry.breach_count} breaches</Badge>}
                      </div>
                    )}

                    {entry.entry_hash && (
                      <div className="mt-2 text-xs text-slate-400 font-mono flex items-center gap-1">
                        <Lock className="w-3 h-3" />{entry.entry_hash.slice(0, 20)}...
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
